import { Controller, FieldError, useFormContext } from 'react-hook-form'
import {
  AlertIcon,
  CopyIcon,
  CurecallBadge,
  CurecallBanner,
  CurecallButton,
  CurecallSelect,
  CurecallSelectItem,
  CurecallTextareaWithVariable,
  ECurecallBadgeColor,
  ECurecallButtonAppearance,
  PenIcon,
  PlusIcon,
  TTextareaVariable,
  TrashIcon,
} from '@honestica/curecall-ui'
import { Trans, useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { getMaxMessageLength, getMessageLength, getNonGsmCharacters, isMessageTooLong } from './sms.util'
import { useVariables } from '../../../../_components/variables-context'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { EAgeGroup, TMessageContentRule, TProgrammedMessageFormValues } from './programmed-message.type'
import { getDoctorName } from 'src/utils/string-formatter.util'
import { twMerge } from 'src/utils/tw-merge.util'
import { useLocale } from 'src/hooks/use-locale'
import { TLabel, TPatientLocale } from 'src/types/locales'

export default function MessageContentRuleCard({
  index,
  onRemove,
  isRemoveDisabled,
  onDuplicate,
  initiallyEditing = false,
}: {
  index: number
  onRemove: () => void
  isRemoveDisabled: boolean
  onDuplicate: () => void
  initiallyEditing?: boolean
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const { activeDoctors, getFollowupById, getMedEventLabelById } = useCurrentProject()
  const { variableStore, contentVariables, defaultContentVariable } = useVariables()
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    clearErrors,
    setError,
    getValues,
  } = useFormContext<TProgrammedMessageFormValues>()

  const [initialContent] = useState<string>(
    () => getValues(`profileRules.${index}.message`)?.[locale as TPatientLocale] ?? '',
  )
  const [isEditing, setIsEditing] = useState(initiallyEditing)
  const [editingSnapshot, setEditingSnapshot] = useState<{
    medicalStaffMemberId: string | null
    ageGroup: EAgeGroup | null
  } | null>(() =>
    initiallyEditing
      ? {
          medicalStaffMemberId: getValues(`profileRules.${index}.medicalStaffMemberId`) ?? null,
          ageGroup: getValues(`profileRules.${index}.ageGroup`) ?? null,
        }
      : null,
  )
  const [variableToShow, setVariableToShow] = useState<TTextareaVariable | null>(null)
  const [specialCharacters, setSpecialCharacters] = useState<string[]>([])
  const [messageLength, setMessageLength] = useState(0)
  const [maxMessageLength, setMaxMessageLength] = useState(() => getMaxMessageLength(''))

  const rule: TMessageContentRule = watch(`profileRules.${index}`)
  const followupId = watch('followupId')
  const medEventIds = watch('medEventIds')

  useEffect(() => {
    if (initialContent) {
      setMessageLength(getMessageLength(initialContent))
      setMaxMessageLength(getMaxMessageLength(initialContent))
    }
  }, [initialContent])

  const medEventsUI = getFollowupById(followupId)?.configUI.medEventsUI?.filter((medEventUI) =>
    medEventIds?.includes(medEventUI.MedEvent.id),
  )
  const canAddLocationAsVariable = medEventsUI?.every((medEventUI) =>
    medEventUI.properties.some((property) => property.fieldName === 'LocationId' && property.isRequired),
  )

  const medEventsWithoutLocation =
    medEventsUI?.filter(
      (medEventUI) => !medEventUI.properties.some((property) => property.fieldName === 'LocationId'),
    ) ?? []

  const medEventsWithoutConvocation =
    medEventsUI?.filter(
      (medEventUI) => !medEventUI.properties.some((property) => property.fieldName === 'convocationDate'),
    ) ?? []

  const messageValues = Object.values(rule?.message ?? {})

  const usedLocationVariableLabels: string[] = []
  if (messageValues.some((msg) => msg.includes('Location.name')))
    usedLocationVariableLabels.push(t('pages.configuration.messages.location-name'))
  if (messageValues.some((msg) => msg.includes('Location.address')))
    usedLocationVariableLabels.push(t('pages.configuration.messages.location-address'))

  const usedConvocationVariableLabels: string[] = []
  if (messageValues.some((msg) => msg.includes('convocationDate; date; fr; DD/MM/YY')))
    usedConvocationVariableLabels.push(t('pages.configuration.messages.convocation-date-and-time'))
  if (messageValues.some((msg) => msg.includes('convocationDate ; date; fr; HH:mm')))
    usedConvocationVariableLabels.push(t('pages.configuration.messages.convocation-time'))
  if (messageValues.some((msg) => msg.includes('convocationDate ; date; fr}')))
    usedConvocationVariableLabels.push(t('pages.configuration.messages.convocation-date'))
  const variableStoreAdaptedToMedEvents = useMemo(() => {
    const result = structuredClone(variableStore)
    if (!canAddLocationAsVariable) {
      delete result?.additionalContext?.patientMedEvent?.Location?.name
    }
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupId, medEventIds])

  const doctorOptions: CurecallSelectItem<string | null>[] = [
    { value: null, label: t('pages.configuration.messages.all-doctors') },
    ...activeDoctors.map((doctor) => ({
      value: String(doctor.id),
      label: getDoctorName(doctor),
    })),
  ]

  const ageOptions: CurecallSelectItem<EAgeGroup | null>[] = [
    { value: null, label: t('pages.configuration.messages.all-ages') },
    { value: EAgeGroup.MINOR, label: t('pages.configuration.messages.minor') },
    { value: EAgeGroup.MAJOR, label: t('pages.configuration.messages.adult') },
  ]

  const selectedDoctorOption = doctorOptions.find(
    (option) => option.value === (rule?.medicalStaffMemberId ?? null),
  )

  const selectedAgeOption = ageOptions.find((option) => option.value === (rule?.ageGroup ?? null))

  const doctorLabel = selectedDoctorOption?.label ?? t('pages.configuration.messages.all-doctors')
  const ageLabel = selectedAgeOption?.label ?? t('pages.configuration.messages.all-ages')

  const ruleErrors = errors.profileRules?.[index]
  const ruleErrorMessage = (ruleErrors?.message as FieldError | undefined)?.message

  const handleChangeMessageContent = (messageContent: string, messageWithAverageExample: string) => {
    // CurecallTextareaWithVariable leaves a stray <br> when the user clears the field,
    // which would otherwise count as 1 character and prevent the required-field validation.
    const normalizedContent = messageContent.replace(/<br\s*\/?>/gi, '').trim() === '' ? '' : messageContent
    const normalizedExample =
      messageWithAverageExample.replace(/<br\s*\/?>/gi, '').trim() === '' ? '' : messageWithAverageExample
    setSpecialCharacters(getNonGsmCharacters(normalizedContent))
    setMessageLength(getMessageLength(normalizedExample))
    const maxLength = getMaxMessageLength(normalizedExample)
    setMaxMessageLength(maxLength)
    if (isMessageTooLong(normalizedExample)) {
      setError(`profileRules.${index}.message`, {
        type: 'maxLength',
        message: t('pages.configuration.messages.too-long-message', { count: maxLength }),
      })
    } else {
      clearErrors(`profileRules.${index}.message`)
    }
    setValue(`profileRules.${index}.message`, { [locale]: normalizedContent } as TLabel)
  }

  const handleDoctorChange = (option: CurecallSelectItem<string | null>) => {
    setValue(`profileRules.${index}.medicalStaffMemberId`, option.value)
  }

  const handleAgeChange = (option: CurecallSelectItem<EAgeGroup | null>) => {
    setValue(`profileRules.${index}.ageGroup`, option.value)
  }

  const handleStartEditing = () => {
    setEditingSnapshot({
      medicalStaffMemberId: rule?.medicalStaffMemberId ?? null,
      ageGroup: rule?.ageGroup ?? null,
    })
    setIsEditing(true)
  }

  const handleConfirmEditing = () => {
    setEditingSnapshot(null)
    setIsEditing(false)
  }

  const handleCancelEditing = () => {
    if (editingSnapshot) {
      setValue(`profileRules.${index}.medicalStaffMemberId`, editingSnapshot.medicalStaffMemberId)
      setValue(`profileRules.${index}.ageGroup`, editingSnapshot.ageGroup)
    }
    setEditingSnapshot(null)
    setIsEditing(false)
  }

  return (
    <div className="c-flex c-flex-col c-gap-2 c-min-w-0 c-max-w-full c-rounded-xl c-border c-border-grey-300">
      <div className="c-flex c-items-center c-justify-between c-bg-grey-50 c-rounded-t-xl c-p-4 c-border-b c-border-grey-300">
        {isEditing ? (
          <div className="c-flex c-items-center c-gap-2">
            <CurecallSelect
              items={doctorOptions}
              selectedItem={selectedDoctorOption}
              onChange={handleDoctorChange}
              placeholder={t('pages.configuration.messages.select-doctor')}
              wrapperClassName="c-w-72 c-relative"
            />
            <PlusIcon className="c-h-3 c-w-3 c-text-grey-500" />
            <CurecallSelect
              items={ageOptions}
              selectedItem={selectedAgeOption}
              onChange={handleAgeChange}
              placeholder={t('pages.configuration.messages.select-age')}
              wrapperClassName="c-w-64 c-relative"
            />
          </div>
        ) : (
          <div className="c-flex c-items-center c-gap-2">
            <span className="c-font-nexa c-text-sm c-text-grey-700">
              {t('pages.configuration.messages.doctor-label')}
            </span>
            <CurecallBadge
              color={
                rule?.medicalStaffMemberId == null
                  ? ECurecallBadgeColor.SMOOTH_GREY
                  : ECurecallBadgeColor.SMOOTH_PRIMARY
              }
            >
              {doctorLabel}
            </CurecallBadge>
            <PlusIcon className="c-h-3 c-w-3 c-text-grey-500" />
            <span className="c-font-nexa c-text-sm c-text-grey-700">
              {t('pages.configuration.messages.age-label')}
            </span>
            <CurecallBadge
              color={
                rule?.ageGroup == null ? ECurecallBadgeColor.SMOOTH_GREY : ECurecallBadgeColor.SMOOTH_PRIMARY
              }
            >
              {ageLabel}
            </CurecallBadge>
          </div>
        )}

        <div className="c-flex c-items-center c-gap-1">
          {isEditing ? (
            <div className="c-flex c-items-center c-gap-1">
              <CurecallButton type="button" onClick={handleConfirmEditing}>
                {t('common.actions.confirm')}
              </CurecallButton>
              <CurecallButton
                type="button"
                appearance={ECurecallButtonAppearance.CANCEL}
                onClick={handleCancelEditing}
              >
                {t('common.actions.cancel')}
              </CurecallButton>
            </div>
          ) : (
            <>
              <CurecallButton
                isGhostButton
                ariaLabelText={t('pages.configuration.messages.edit-rule')}
                iconType={<PenIcon className="c-text-grey-600" />}
                onClick={handleStartEditing}
                type="button"
              />
              <CurecallButton
                isGhostButton
                ariaLabelText={t('pages.configuration.messages.duplicate-rule')}
                iconType={<CopyIcon className="c-text-grey-600" />}
                onClick={onDuplicate}
                type="button"
              />
              <CurecallButton
                isGhostButton
                ariaLabelText={t('pages.configuration.messages.remove-rule')}
                iconType={<TrashIcon className={isRemoveDisabled ? 'c-text-grey-400' : 'c-text-grey-600'} />}
                onClick={onRemove}
                isDisabled={isRemoveDisabled}
                type="button"
              />
            </>
          )}
        </div>
      </div>

      <div className="c-px-4 c-pb-6 c-min-w-0 [&_[role=textbox]]:c-break-words">
        <Controller
          control={control}
          name={`profileRules.${index}.message`}
          rules={{ required: t('pages.configuration.messages.message-content-error-message') }}
          render={() => (
            <CurecallTextareaWithVariable
              variableStore={variableStoreAdaptedToMedEvents}
              contentDropdownVariable={{
                label: t('pages.configuration.messages.content-name'),
                items: contentVariables,
                defaultVariable: defaultContentVariable,
              }}
              onChange={handleChangeMessageContent}
              defaultValue={initialContent}
              maxLength={maxMessageLength}
              messageLength={messageLength}
              isRequired={true}
              label={t('pages.configuration.messages.message-for-rule-placeholder', {
                doctor: doctorLabel,
                age: ageLabel,
              })}
              tooltipContent={t('pages.configuration.messages.to-add-a-variable-instructions')}
              tooltipPosition="right"
              variablesExplainationText={t('pages.configuration.messages.click-on-variable')}
              variableToShow={variableToShow}
              setVariableToShow={setVariableToShow}
              modalTitle={t('pages.configuration.messages.variable-information', {
                label: variableToShow?.label,
              })}
              modalCloseButtonAriaLabel={t('pages.configuration.messages.variable-information', {
                label: variableToShow?.label,
              })}
              modalExplainationsText={t('pages.configuration.messages.variable-information-explainations', {
                label: variableToShow?.label,
              })}
              modalForExampleText={t('pages.configuration.messages.for-example')}
              modalWillGiveText={t('pages.configuration.messages.for-example')}
              errorMessage={ruleErrorMessage}
              showVariableListOnFocusOnly
            />
          )}
        />

        {specialCharacters.length > 0 && (
          <div
            className={twMerge(
              'c-flex c-items-start c-gap-2 c-mt-2 c-font-nexa c-text-xs c-text-orange-600',
              // the textarea error message is absolutely positioned and would overlap this warning
              ruleErrorMessage && 'c-mt-8',
            )}
          >
            <AlertIcon className="c-h-4 c-w-4 c-shrink-0 c-text-orange-500" />
            <span>
              {t('pages.configuration.messages.warning-special-characters', {
                specialCharacters: specialCharacters.join(', '),
              })}
            </span>
          </div>
        )}

        {medEventsWithoutLocation.length > 0 && usedLocationVariableLabels.length > 0 && (
          <CurecallBanner appearance="WARNING" className="c-mt-6 c-mb-0">
            <Trans
              i18nKey="pages.configuration.messages.location-variables-warning"
              values={{
                variables: usedLocationVariableLabels.join(', '),
                rdv: medEventsWithoutLocation
                  .map((medEventUI) => getMedEventLabelById(medEventUI.MedEvent.id))
                  .join(', '),
              }}
              components={{ bold: <strong />, br: <br /> }}
            />
          </CurecallBanner>
        )}

        {medEventsWithoutConvocation.length > 0 && usedConvocationVariableLabels.length > 0 && (
          <CurecallBanner appearance="WARNING" className="c-mt-6 c-mb-0">
            <Trans
              i18nKey="pages.configuration.messages.convocation-variables-warning"
              values={{
                variables: usedConvocationVariableLabels.join(', '),
                rdv: medEventsWithoutConvocation
                  .map((medEventUI) => getMedEventLabelById(medEventUI.MedEvent.id))
                  .join(', '),
              }}
              components={{ bold: <strong />, br: <br /> }}
            />
          </CurecallBanner>
        )}
      </div>
    </div>
  )
}
